<script lang="ts">
  import { onMount, onDestroy } from "svelte"
  import { slide } from "svelte/transition"
  import { SyncLoader } from "svelte-loading-spinners"
  import { xhr, toast, handleVideoLinkClick } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Post from "./Post.svelte"
  import Members from "./Members.svelte"
  import Loading from "$app/components/Loading.svelte"
  import { appStore } from "$app/store.svelte"
  import { ChevronDown } from "lucide-svelte"
  import NewPost from "./NewPost.svelte"

  interface Props {
    channel: ForumChannelType
    channels: ForumChannelType[]
  }

  let { channel, channels }: Props = $props()

  let isLoading = $state(true)

  let showAdminSettings = $state(false)

  onMount(async () => {
    loadLatestPosts()
  })

  let saving = $state(false)

  let community_read_only = $state(appStore.plan.community_read_only)

  async function savePlan(): Promise<void> {
    saving = true

    try {
      await xhr.patch(`/plans/${appStore.plan.id}.json`, {
        plan: {
          community_read_only: community_read_only
        }
      })

      appStore.plan.community_read_only = community_read_only
      showAdminSettings = !community_read_only
      toast("Community member access was updated", "success")
    } catch (e) {
      toast("There was an error updating member access", "error")
    } finally {
      saving = false
    }
  }

  let latestPosts = $state([])

  async function loadLatestPosts(): Promise<void> {
    isLoading = true

    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/community_posts/latest.json`
      )
      appStore.permission_level = resp.data.permission_level

      latestPosts = resp.data.posts
    } catch (e) {
      isLoading = false
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the latest posts", "error")
    } finally {
      isLoading = false
    }
  }

  onMount(() => {
    let postsElement = document.addEventListener(
      "click",
      handleVideoLinkClick,
      { passive: false }
    )
  })
  onDestroy(() => {
    document.removeEventListener("click", handleVideoLinkClick)
  })
</script>

<div class="bg-lwp-pale-grey py-4 px-6">
  <h1 class="text-3xl font-bold mt-4">
    Welcome to the Lead With Purpose Community
  </h1>

  <div class="relative mt-4">
    <div class="bg-white py-4 px-6 shadow-lg">
      <h1 class="text-3xl font-bold">{channel.name}</h1>
      <NewPost {channel} {channels} />
    </div>
  </div>

  <div class="py-4">
    {#if appStore.plan && appStore.current_user.role === "admin"}
      <div class="grid grid-cols-1 mb-6 rounded border border-gray-200 shadow">
        <div
          class="flex items-center justify-between gap-x-4 border-b border-gray-900/5 bg-zinc-100 px-6 py-4"
        >
          <div class="flex items-center">
            <img
              src={appStore.plan.logo_url}
              alt={appStore.plan.name}
              class="h-8 w-8 flex-none rounded-full bg-white object-cover ring-1 ring-gray-900/10"
            />
            <div class="ml-2 text-sm font-medium leading-6 text-gray-900">
              {appStore.plan.name} Plan
            </div>
          </div>
          <div class="text-base font-bold text-gray-700">
            <button
              onclick={() => (showAdminSettings = !showAdminSettings)}
              class="flex items-center"
            >
              Community Admin Settings
              <span class:rotate-180={showAdminSettings}>
                <ChevronDown class="size-8 text-gray-600 ml-1" />
              </span>
            </button>
          </div>
        </div>

        {#if showAdminSettings}
          <div
            class="divide-y divide-gray-100 px-6 py-6 text-sm leading-6 bg-white"
            transition:slide
          >
            <p class="py-3 text-sm text-gray-600">
              By default your plan members can add new posts and comment on
              existing posts once they have agreed to the Community terms.
              However, if you would prefer for your members to have read-only
              access and only view posts, then please update the setting below.
            </p>
            <div class="flex justify-center items-center gap-x-4 py-3">
              <label
                class="mt-0 pt-0 text-base font-medium text-gray-700"
                for="community_read_only"
              >
                Community Plan Member Access
              </label>
              <select
                name="community_read_only"
                id="community_read_only"
                class="border w-64 border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-blue-500 sm:text-sm"
                bind:value={community_read_only}
              >
                <option value={false}>Full Access (Post and Comment)</option>
                <option value={true}>Read-only (View Only)</option>
              </select>
              <Loading loading={saving}>
                <button
                  onclick={savePlan}
                  disabled={saving}
                  class="rounded bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 whitespace-nowrap"
                >
                  Save Settings
                </button>
              </Loading>
            </div>
          </div>
          <div class="px-6 py-4 bg-white border-t border-gray-200">
            <Members />
          </div>
        {/if}
      </div>
    {/if}
  </div>

  <h1 class="mt-8 text-2xl font-bold">Latest Posts</h1>
  {#if isLoading}
    <div class="py-12 flex justify-center w-full">
      <SyncLoader size="240" color="#7572d2" unit="px" duration="1s" />
    </div>
  {:else}
    <ul role="list" class="py-6 space-y-4 overflow-y-auto overflow-x-hidden">
      {#each latestPosts as post}
        <Post {post} readOnly={true} showPinned={false} />
      {:else}
        <div class="px-8 py-4 border border-indigo-50 rounded shadow-lg">
          <p class="text-lg font-bold">No Posts at this time</p>
        </div>
      {/each}
    </ul>
  {/if}
</div>
